// Copyright 2022, Imprivata, Inc.  All rights reserved.
import { Flex, Layout } from 'antd';
import { type PropsWithChildren, useEffect } from 'react';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { Route, Routes, useSearchParams } from 'react-router';
import BlueHexAnimation from '../../assets/svg/blueHexAnimation.svg';
import { PlatformProvider } from '../../context/platform/PlatformContextProvider';
import AccountPage from '../../pages/account/AccountPage';
import EnrollPage from '../../pages/enroll/EnrollPage';
import LandingPage from '../../pages/landing/LandingPage';
import LaunchPage from '../../pages/launch/LaunchPage';
import { preloadSvg } from '../../utils/SvgPreloader';
import CustomerNameComponent from '../customer/CustomerNameComponent';
import { ErrorComponent } from '../error/ErrorComponent';
import './App.less';
import { Content, Header } from 'antd/lib/layout/layout';
import ConsentPage from '../../pages/consent/ConsentPage';
import PhoneVerificationPage from '../../pages/phone-verification/PhoneVerificationPage';
import IdentifyPage from '../../pages/identify/IdentifyPage';
import VerifyPage from '../../pages/verify/VerifyPage';
import ServiceDeskPage from '../../pages/service-desk/ServiceDeskPage';

const handleError = ({ error }: FallbackProps) => {
  return <ErrorComponent cause={error.message} />;
};

const AppLayout = ({ children }: PropsWithChildren) => (
  <Layout className="app-layout">
    <Header>
      <CustomerNameComponent />
    </Header>
    <Content className="app-body-flex-container">
      <Flex align="center" justify="center" vertical>
        <ErrorBoundary fallbackRender={handleError}>{children}</ErrorBoundary>
      </Flex>
    </Content>
  </Layout>
);

// Every route that needs the platform context (coding context and feature flags).
const PlatformRoutes = () => {
  const [searchParams] = useSearchParams();
  return (
    <Routes>
      <Route path={'/'} element={<LandingPage />} />
      <Route path={'/launch'} element={<LaunchPage />} />
      <Route path={'/account'} element={<AccountPage />} />
      <Route path={'/enroll'} element={<EnrollPage />} />
      <Route path={'/consent'} element={<ConsentPage />} />
      <Route path={'/phone-verification'} element={<PhoneVerificationPage />} />
      <Route path={'/verify'} element={<VerifyPage />} />
      <Route path={'/identify'} element={<IdentifyPage />} />
      <Route path={'*'} element={<ErrorComponent cause={ searchParams.get('error-code') ?? "not-found"} />} />
    </Routes>
  );
};

const App = () => {
  // Preload critical SVGs early in the app lifecycle
  useEffect(() => {
    // Preload the loading animation SVG as it's used frequently
    preloadSvg(BlueHexAnimation).catch((error) => {
      console.warn('Failed to preload loading animation SVG:', error);
    });
  }, []);
  return (
    <Routes>
      {/* Outside PlatformProvider on purpose: the Link no longer valid screen must still show
          when the coding-context fetch fails or never completes (PSA-10679). */}
      <Route
        path={'/service-desk'}
        element={
          <AppLayout>
            <ServiceDeskPage />
          </AppLayout>
        }
      />
      <Route
        path={'*'}
        element={
          <PlatformProvider>
            <AppLayout>
              <PlatformRoutes />
            </AppLayout>
          </PlatformProvider>
        }
      />
    </Routes>
  );
};
export default App;
